import { Pagination, SuccessResponse } from "common/common.interface";
import { useAppSelector, useSnack } from "common/hooks";
import { useInfiniteQueryWithLoading } from "common/hooks/src/api/useInfiniteQueryWithLoading";
import { useMutationWithLoading } from "common/hooks/src/api/useMutationWithLoading";
import { useQueryWithLoading } from "common/hooks/src/api/useQueryWithLoading";
import {
  FamilyWithMembers,
  CreateFamilyPayload,
  Family,
  FamilyBalance,
} from "modules/family/types/family.interface";
import { Client } from "modules/client/types/client.interface";
import {
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
  useQueryClient,
} from "react-query";
import { useNavigate } from "react-router-dom";
import { FAMILY_ROUTES } from "./family.api.enum";
import { getFamilyBackend } from "./backends";

export const useGetFamilies = (
  search: string | undefined,
  limit: number = 20
): UseInfiniteQueryResult<Pagination<FamilyWithMembers>> => {
  const setSnack = useSnack();

  const getFamilies = async ({
    pageParam = 1,
  }): Promise<Pagination<FamilyWithMembers>> => {
    const familyBackend = await getFamilyBackend();
    return await familyBackend.getFamilies(pageParam, limit, search);
  };

  return useInfiniteQueryWithLoading(
    [FAMILY_ROUTES.GET_ALL, search],
    getFamilies,
    {
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
      enabled: true,
    }
  );
};

export const useGetFamily = (
  id: string | undefined
): UseQueryResult<FamilyWithMembers> => {
  const setSnack = useSnack();

  const getFamily = async (): Promise<FamilyWithMembers> => {
    const familyBackend = await getFamilyBackend();
    return await familyBackend.getFamily(id!);
  };

  return useQueryWithLoading(FAMILY_ROUTES.GET + id, () => getFamily(), {
    onError() {
      setSnack({
        title: "Erreur interne!",
        severityType: "error",
      });
    },
    enabled: !!id,
  });
};

export const useGetFamilyBalance = (
  familyId: string | undefined
): UseQueryResult<FamilyBalance> => {
  const setSnack = useSnack();

  const getBalance = async (): Promise<FamilyBalance> => {
    const familyBackend = await getFamilyBackend();
    return await familyBackend.getFamilyBalance(familyId!);
  };

  return useQueryWithLoading(
    [FAMILY_ROUTES.GET_BALANCE, familyId],
    () => getBalance(),
    {
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
      enabled: !!familyId,
    }
  );
};

export const useCreateFamily = (): UseMutationResult<
  Family,
  Error,
  CreateFamilyPayload
> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAppSelector((state) => state.user.user);

  const createFamily = async (
    payload: CreateFamilyPayload
  ): Promise<Family> => {
    const familyBackend = await getFamilyBackend();
    return await familyBackend.createFamily(user.workspaceId, payload);
  };

  return useMutationWithLoading(
    (payload: CreateFamilyPayload) => createFamily(payload),
    {
      onSuccess() {
        setSnack({
          title: "Famille créée avec succès!",
          severityType: "success",
        });

        queryClient.refetchQueries(FAMILY_ROUTES.GET_ALL);

        navigate("/admin/family");
      },
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useUpdateFamily = (
  id: string | undefined
): UseMutationResult<Family, Error, CreateFamilyPayload> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const updateFamily = async (
    payload: CreateFamilyPayload
  ): Promise<Family> => {
    const familyBackend = await getFamilyBackend();
    return await familyBackend.updateFamily(id!, payload);
  };

  return useMutationWithLoading(
    (payload: CreateFamilyPayload) => updateFamily(payload),
    {
      onSuccess: (data) => {
        setSnack({
          title: "Famille modifiée avec succès!",
          severityType: "success",
        });

        queryClient.invalidateQueries([FAMILY_ROUTES.GET, id]);
        queryClient.invalidateQueries([FAMILY_ROUTES.GET_ALL]);

        navigate("/admin/family");
      },
      onError() {
        setSnack({
          title: "Erreur interne!",
          severityType: "error",
        });
      },
    }
  );
};

export const useArchiveFamily = (): UseMutationResult<
  SuccessResponse,
  Error,
  string
> => {
  const setSnack = useSnack();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const archiveFamily = async (id: string): Promise<SuccessResponse> => {
    const familyBackend = await getFamilyBackend();
    return await familyBackend.archiveFamily(id);
  };

  return useMutationWithLoading((id: string) => archiveFamily(id), {
    onSuccess() {
      setSnack({
        title: "Famille supprimée avec succès!",
        severityType: "success",
      });

      queryClient.refetchQueries(FAMILY_ROUTES.GET_ALL);

      navigate("/admin/family");
    },
    onError() {
      setSnack({
        title: "Erreur interne!",
        severityType: "error",
      });
    },
  });
};

export const useAddFamilyMember = (
  familyId: string | undefined
): UseMutationResult<Client, Error, string> => {
  const setSnack = useSnack();
  const queryClient = useQueryClient();

  const addMember = async (clientId: string): Promise<Client> => {
    const familyBackend = await getFamilyBackend();
    return await familyBackend.addMember(familyId!, clientId);
  };

  return useMutationWithLoading((clientId: string) => addMember(clientId), {
    onSuccess() {
      setSnack({
        title: "Membre ajouté avec succès!",
        severityType: "success",
      });

      queryClient.invalidateQueries([FAMILY_ROUTES.GET, familyId]);
      queryClient.invalidateQueries([FAMILY_ROUTES.GET_ALL]);
    },
    onError() {
      setSnack({
        title: "Erreur interne!",
        severityType: "error",
      });
    },
  });
};

export const useRemoveFamilyMember = (
  familyId: string | undefined
): UseMutationResult<Client, Error, string> => {
  const setSnack = useSnack();
  const queryClient = useQueryClient();

  const removeMember = async (clientId: string): Promise<Client> => {
    const familyBackend = await getFamilyBackend();
    return await familyBackend.removeMember(familyId!, clientId);
  };

  return useMutationWithLoading((clientId: string) => removeMember(clientId), {
    onSuccess() {
      setSnack({
        title: "Membre retiré avec succès!",
        severityType: "success",
      });

      queryClient.invalidateQueries([FAMILY_ROUTES.GET, familyId]);
      queryClient.invalidateQueries([FAMILY_ROUTES.GET_ALL]);
    },
    onError() {
      setSnack({
        title: "Erreur interne!",
        severityType: "error",
      });
    },
  });
};
